import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import { withRouter } from 'next/router';
import { Button } from '@material-ui/core';

import PageBase from '../../components/layout/PageBase';
import Swiper from '../../components/utility/Swiper';
import RandomForm from '../../components/pages/random/RandomForm';
import * as types from '../../actions/actionTypes';
import {
  fetchRandomListingCount,
  fetchRandomListing,
  setVisited,
} from '../../actions/randomHome';
import { isMobile } from '../../lib/getDeviceSize';
import { localStorageTracker } from '../../lib/helpers';
import { storageKeys } from '../../lib/storage';
import { FiltersIcon } from '../../components/utility/Icons';
import { fabButtonStyles } from '../../pages/search';
import { defaultMaxPrice, defaultState } from '../../reducers/randomHome';

import styles from '../../styles/sass/pages/Random.module.scss';
import LogoLoader from '../../components/utility/LogoLoader';

export interface Props {
  count: number;
  classes: any;
  dispatch: any;
  fetchRandomListingCount: Function;
  fetchRandomListing: Function;
  listings: any;
  location: string;
  maxPrice: number;
  router: any;
  showSignupModal: Function;
  token: string;
  visited: boolean;
}
export interface State {
  currentIndex: number;
  height: number;
  showSettings: boolean;
}

const HEADER_HEIGHT = 70;

export class Random extends React.Component<Props, State> {
  state = {
    currentIndex: 0,
    height: 500,
    showSettings: false,
  };

  componentDidMount() {
    this.setDefaultParams();

    const q = this.props.router.query;
    const price = q.price ? q.price : defaultMaxPrice;
    const location = q.location ? q.location : defaultState;
    this.props.dispatch({
      maxPrice: price,
      randomHomeState: location,
      type: types.SetRandomHomeParams,
    });
    this.fetchNewRandomHouses(price, location, this.props.count);
    if (typeof window !== 'undefined') {
      //Show settings dialog on mount.
      this.showSettings();

      this.updateWindowDimensions();
      window.addEventListener('resize', this.updateWindowDimensions);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  updateWindowDimensions = () => {
    this.setState({
      height: window.innerHeight,
    });
  };

  showSignupModal = () => {
    this.props.dispatch({
      postAuthPath: '/random',
      signupSubtext: `Sign up for Torii to save your favorite properties, plus free access to property data, trends, and the best way to buy a home.`,
      type: types.OpenSignupDialog,
    });
  };

  setDefaultParams = () => {
    const q = this.props.router.query;
    this.props.dispatch({
      maxPrice: q.price ? q.price : defaultMaxPrice,
      randomHomeState: q.location ? q.location : defaultState,
      type: types.SetRandomHomeParams,
    });
  };

  fetchNewRandomHouses = (
    maxPrice: number,
    location: string,
    count: number,
    reset: boolean = false,
  ) => {
    if (!this.props.token) {
      localStorageTracker(
        storageKeys.noOfRandomHomeVisits,
        1,
        () => {
          this.props.fetchRandomListingCount(maxPrice, location);
          this.props.fetchRandomListing(maxPrice, count, location, null, reset);
          this.setState({
            currentIndex: 0,
          });
        },
        () => {
          this.showSignupModal();
        },
        true,
      );
    } else {
      this.props.fetchRandomListingCount(maxPrice, location);
      this.props.fetchRandomListing(
        maxPrice,
        count,
        location,
        this.props.token,
        reset,
      );
    }
  };

  handleSubmit = (maxPrice: number, location: string) => {
    const count = 10;
    this.fetchNewRandomHouses(maxPrice, location, count, true);
    if (!this.props.visited) {
      this.props.dispatch(setVisited());
    }
  };

  handleFetchMore = () => {
    this.fetchNewRandomHouses(this.props.maxPrice, this.props.location, 10);
  };

  showSettings = () => {
    this.setState({
      showSettings: true,
    });
  };

  closeSettings = () => {
    this.setState({
      showSettings: false,
    });
  };

  render() {
    const { classes, dispatch, listings } = this.props;
    const { height, showSettings } = this.state;
    return (
      <PageBase title="Find a random real estate listing in Massachusetts | Torii">
        <div
          className={`page-container ${styles.randomHomeContainer} random-home-container col-xs-12 center-xs`}
          style={{ height: height - HEADER_HEIGHT }}
        >
          <RandomForm
            dispatch={dispatch}
            open={showSettings}
            handleClose={this.closeSettings}
            handleSubmit={this.handleSubmit}
          />
          {listings.length === 0 && (
            <div
              className={`${styles.homeInner} ${styles.loadingContainer} row col-xs center-xs`}
            >
              <div className="middle-xs center-xs">
                <LogoLoader imageProps={{ height: 250, width: 250 }} />
              </div>
            </div>
          )}
          <Button
            className={styles.settings}
            classes={{ label: classes.button }}
            onClick={this.showSettings}
          >
            <FiltersIcon dark />
            <p className={classes.buttonText}>Filters</p>
          </Button>
          {listings.length > 0 && (
            <Swiper
              listings={listings}
              hideFilter
              fetchMore={this.handleFetchMore}
            />
          )}
        </div>
      </PageBase>
    );
  }
}

const mapStateToProps = (state: any) => ({
  count: state.randomHome.count,
  listings: state.randomHome.listings,
  location: state.randomHome.location,
  maxPrice: state.randomHome.maxPrice,
  token: state.auth.token,
  visited: state.randomHome.visited,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  fetchRandomListingCount(maxPrice: number, location: string) {
    dispatch(fetchRandomListingCount(maxPrice, location));
  },
  fetchRandomListing(
    maxPrice: number,
    count: number,
    location: string,
    token?: string,
    reset?: boolean,
  ) {
    dispatch(fetchRandomListing(maxPrice, count, location, token, reset));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(fabButtonStyles)(withRouter(Random)));
